import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { apiClient, useAuthStore } from '@dukene/shared';
import { format, parseISO } from 'date-fns';

interface RevenueData {
  date: string;
  revenue: number;
  orders: number;
}

type TimeRange = 'day' | 'week' | 'month' | 'quarter' | 'year';

const RANGES: { label: string; value: TimeRange }[] = [
  { label: 'Daily', value: 'day' },
  { label: 'Weekly', value: 'week' },
  { label: 'Monthly', value: 'month' },
  { label: 'Quarterly', value: 'quarter' },
  { label: 'Yearly', value: 'year' },
];

export default function RevenueChart() {
  const [range, setRange] = useState<TimeRange>('month');
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  const { data: chartData, isLoading } = useQuery<RevenueData[]>({
    queryKey: ['revenue-chart', activeStoreId, range],
    queryFn: async () => {
      return apiClient.get<RevenueData[]>(`/analytics/sales?range=${range}`);
    },
    enabled: !!activeStoreId,
  });

  if (isLoading) {
    return (
      <div className="h-[380px] w-full bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 flex flex-col gap-4 animate-pulse">
        <div className="h-6 w-48 bg-[#F6F6F7] dark:bg-slate-800 rounded" />
        <div className="flex-1 bg-[#F6F6F7] dark:bg-slate-800 rounded-lg" />
      </div>
    );
  }

  const formattedData =
    chartData?.map((item) => {
      let label = item.date;
      try {
        const date = parseISO(item.date);
        switch (range) {
          case 'day':
            label = format(date, 'EEE, MMM dd');
            break;
          case 'week': {
            const start = date;
            const end = new Date(date);
            end.setDate(end.getDate() + 6);
            label = `${format(start, 'MMM dd')} - ${format(end, 'MMM dd')}`;
            break;
          }
          case 'month':
            label = format(date, 'MMMM yyyy');
            break;
          case 'quarter':
          case 'year':
            label = item.date;
            break;
        }
      } catch {
        // Fallback to raw string
      }

      return {
        ...item,
        formattedDate: label,
      };
    }) || [];

  return (
    <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
            Store Revenue Performance
          </h2>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Gross sales trend over the selected reporting timeframe
          </p>
        </div>

        <div className="flex items-center p-0.5 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg border border-[#D2D5D9] dark:border-slate-800">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                range === r.value
                  ? 'bg-white dark:bg-slate-800 text-[#006838] dark:text-emerald-400 shadow-xs border border-[#D2D5D9] dark:border-slate-700'
                  : 'text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-white'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={formattedData}>
            <defs>
              <linearGradient id="colorRevenuePolaris" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#006838" stopOpacity={0.18} />
                <stop offset="95%" stopColor="#006838" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E1E4E8" />
            <XAxis
              dataKey="formattedDate"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#8C9196', fontSize: 10, fontWeight: 600 }}
              dy={8}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#8C9196', fontSize: 10, fontWeight: 600 }}
              tickFormatter={(value) => (value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #D2D5D9',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                padding: '10px',
                fontSize: '12px',
              }}
              itemStyle={{ color: '#006838', fontWeight: 600 }}
              labelStyle={{ color: '#202223', marginBottom: '4px', fontWeight: 700 }}
              formatter={(value: number) => [`KES ${value.toLocaleString()}`, 'Revenue']}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#006838"
              strokeWidth={2.5}
              fillOpacity={1}
              fill="url(#colorRevenuePolaris)"
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
